/**
 * TosGate - Route wrapper that enforces Terms of Service acceptance.
 *
 * Reads `user.acceptedTosVersion` and `activeTosVersion` from AuthContext and:
 * - If `acceptedTosVersion` is missing → redirects to `/tos/accept`
 * - If `acceptedTosVersion` is present but lower than active → renders TosReAcceptModal
 * - Otherwise → renders children
 *
 * Bypasses enforcement for allowlisted routes: /auth/callback, /tos/accept, logout.
 *
 * Requirements: 4.1, 4.5, 5.1, 5.9, 10.1
 */

import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { compareSemver } from '@/utils/semver';
import { TOS_FALLBACK_VERSION } from '@/services/auth';
import { TosReAcceptModal } from './TosReAcceptModal';

/** Routes that bypass ToS gate enforcement. */
// Includes the acceptance page itself, auth callback, logout, and the public
// legal pages — the user must be able to read Terms/Privacy (opened from the
// acceptance screen) without being bounced back to the gate.
const ALLOWLISTED_PATHS = ['/auth/callback', '/tos/accept', '/logout', '/terms', '/privacy'];

interface TosGateProps {
  children: ReactNode;
}

export function TosGate({ children }: TosGateProps) {
  const { user, activeTosVersion, isLoading } = useAuth();
  const location = useLocation();

  // Bypass for allowlisted routes
  if (ALLOWLISTED_PATHS.some((path) => location.pathname.startsWith(path))) {
    return <>{children}</>;
  }

  // If no user or still loading, render children
  // (AuthGuard handles unauthenticated state separately)
  if (!user || isLoading) {
    return <>{children}</>;
  }

  // Use activeTosVersion from context, or fall back to the shared constant.
  const tosVersion = activeTosVersion || TOS_FALLBACK_VERSION;

  const acceptedVersion = user.acceptedTosVersion;

  // No accepted version → redirect to acceptance page
  if (!acceptedVersion) {
    return <Navigate to="/tos/accept" replace />;
  }

  // Accepted version is lower than active → show re-acceptance modal
  if (compareSemver(acceptedVersion, tosVersion) < 0) {
    return <TosReAcceptModal>{children}</TosReAcceptModal>;
  }

  // Version matches or is higher → render children normally
  return <>{children}</>;
}
